import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  InputAdornment
} from '@mui/material';
import {
  Search,
  FilterList,
  Person,
  Visibility
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { blogService } from '../services/firebase';
import { Blog } from '../types';
import { uploadSampleData } from '../utils/uploadSampleData';
import { useTranslation } from 'react-i18next';
import { getTranslatedData } from '../utils/getTranslatedData';

const BlogPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const blogsData = await blogService.getBlogs();
      setBlogs(blogsData);
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError('Blog yazıları yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Kategorileri bloglardan çıkar
  const categories = Array.from(new Set(blogs.map(blog => getTranslatedData(blog.category)).filter(Boolean)));

  // Filtreleme
  const filteredBlogs = blogs.filter(blog => {
    const lang = i18n.language;
    const title = getTranslatedData(blog.title, lang);
    const content = getTranslatedData(blog.content, lang);
    const excerpt = getTranslatedData(blog.excerpt, lang);

    const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (content && content.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (excerpt && excerpt.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = !selectedCategory || getTranslatedData(blog.category, lang) === selectedCategory;
    
    // Yayın durumu kontrolü - daha esnek
    let isPublished = true;
    if (blog.isPublished !== undefined) {
      isPublished = blog.isPublished;
    } else if (blog.status !== undefined) {
      isPublished = blog.status === 'published' || blog.status === 'active';
    }
    
    const shouldShow = matchesSearch && matchesCategory && isPublished;
    
    return shouldShow;
  });

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        backgroundColor: 'var(--light-gray-white)'
      }}>
        <CircularProgress sx={{ color: 'var(--light-blue-cta)' }} />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Blog - HatTravel</title>
        <meta name="description" content="Seyahat rehberi ve blog yazıları." />
      </Helmet>
      
      <Box sx={{ 
        backgroundColor: 'var(--light-gray-white)',
        minHeight: '100vh'
      }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography 
              variant="h1" 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '3rem', md: '4rem' },
                color: 'var(--navy-blue)',
                mb: 2
              }}
            >
              {t('home.blog.page_title', 'Blog & Seyahat Rehberi')}
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ mb: 4, fontWeight: 300 }}>
              {filteredBlogs.length > 0
                ? t('blogPage.found_count', { count: filteredBlogs.length, defaultValue: `${filteredBlogs.length} blog yazısı bulundu` })
                : t('blogPage.no_posts', 'Uygun blog yazısı bulunamadı')}
            </Typography>
          </Box>

          {/* Filters */}
          <Card sx={{ 
            mb: 6, 
            p: 4, 
            backgroundColor: 'white',
            borderRadius: 4,
            boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
          }}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                placeholder={t('blogPage.search_placeholder', 'Blog ara...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: 'var(--light-blue-cta)' }} />
                    </InputAdornment>
                  )
                }}
                sx={{ 
                  minWidth: 280,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                  }
                }}
              />
              
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>{t('blogPage.category_label', 'Kategori')}</InputLabel>
                <Select
                  value={selectedCategory}
                  label={t('blogPage.category_label', 'Kategori')}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  sx={{
                    borderRadius: 3,
                  }}
                >
                  <MenuItem value="">{t('blogPage.all_categories', 'Tümü')}</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('');
                }}
                sx={{ 
                  borderRadius: 3,
                  borderColor: 'var(--light-blue-cta)',
                  color: 'var(--light-blue-cta)',
                  '&:hover': {
                    borderColor: 'var(--cobalt-blue)',
                    backgroundColor: 'var(--cobalt-blue)',
                    color: 'white',
                  }
                }}
              >
                {t('blogPage.clear_filters', 'Filtreleri Temizle')}
              </Button>
            </Box>
          </Card>

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 4, borderRadius: 3 }}>
              {error}
            </Alert>
          )}

          {/* Blogs Grid */}
          {filteredBlogs.length > 0 ? (
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, 
              gap: 4,
              pb: 6
            }}>
              {filteredBlogs.map((blog) => (
                <Card
                  key={blog.id}
                  component={Link}
                  to={`/blog/${blog.id}`}
                  sx={{
                    height: '100%',
                    textDecoration: 'none',
                    color: 'inherit',
                    backgroundColor: 'white',
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      '& .blog-image': {
                        transform: 'scale(1.05)'
                      }
                    },
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Box sx={{ position: 'relative', overflow: 'hidden', height: 250 }}>
                    <CardMedia
                      component="img"
                      image={blog.featuredImage || 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2074&q=80'}
                      alt={getTranslatedData(blog.title)}
                      className="blog-image"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        display: 'block'
                      }}
                    />
                    {blog.category && (
                      <Box sx={{
                        position: 'absolute',
                        top: 16,
                        left: 16,
                        zIndex: 2
                      }}>
                        <Chip
                          label={getTranslatedData(blog.category)}
                          sx={{
                            background: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(10px)',
                            color: '#667eea',
                            fontWeight: 500,
                            border: '1px solid rgba(102, 126, 234, 0.3)'
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                  
                  <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {blog.category && (
                      <Chip 
                        label={getTranslatedData(blog.category)}
                        size="small"
                        sx={{ 
                          alignSelf: 'flex-start',
                          mb: 2,
                          backgroundColor: 'rgba(102, 126, 234, 0.1)',
                          color: 'var(--light-blue-cta)',
                          fontWeight: 500
                        }}
                      />
                    )}
                    <Typography gutterBottom variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1.5, flexGrow: 1, color: 'var(--navy-blue)' }}>
                      {getTranslatedData(blog.title)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {getTranslatedData(blog.excerpt) || `${getTranslatedData(blog.content)?.substring(0, 100)}...`}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto', pt: 2, borderTop: '1px solid #eee' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person sx={{ fontSize: 18, color: '#667eea' }} />
                        <Typography variant="body2" color="#34495e" sx={{ fontWeight: 500 }}>
                          {blog.author}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Visibility sx={{ fontSize: 18, color: '#667eea' }} />
                        <Typography variant="body2" color="#34495e" sx={{ fontWeight: 500 }}>
                          {blog.viewCount || 0}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Card sx={{ 
              textAlign: 'center', 
              py: 8,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: 4,
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)'
            }}>
              <Typography variant="h5" color="#7f8c8d" gutterBottom sx={{ fontWeight: 300 }}>
                Aradığınız kriterlere uygun blog yazısı bulunamadı
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('');
                }}
                sx={{ 
                  mt: 2,
                  borderRadius: 3,
                  borderColor: '#667eea',
                  color: '#667eea',
                  '&:hover': {
                    borderColor: '#5a6fd8',
                    backgroundColor: 'rgba(102, 126, 234, 0.1)'
                  }
                }}
              >
                {t('blogPage.clear_filters', 'Filtreleri Temizle')}
              </Button>
            </Card>
          )}

          {/* Results Count */}
          <Box sx={{ textAlign: 'center', mt: 6, pb: 6 }}>
            <Typography variant="body1" color="#7f8c8d" sx={{ fontWeight: 300 }}>
              {t('blogPage.displayed_count', { count: filteredBlogs.length, defaultValue: `${filteredBlogs.length} blog yazısı gösteriliyor` })}
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default BlogPage; 